<template>
  <div :class="['profile-lock', presentation === 'page' ? 'is-page' : 'is-modal']">
    <div class="lock-card">
      <GamIcon class="lock-icon" :name="GamIconName.HANDSHAKE" size="32px" is-disabled />

      <div class="lock-name">@{{ username }}</div>

      <p class="lock-message">{{ message }}</p>

      <GamButton
        v-if="offersPremium"
        :variant="GamButtonVariant.SECONDARY"
        :size="GamButtonSize.SMALL"
        is-centered
        @click="openPremiumProfileUnlock"
      >
        {{ localize('profile.lock.premium') }}
      </GamButton>

      <button type="button" class="lock-close" @click="dismiss">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize, localizeWithValues } from '@/core/gambits';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  closeProfileLock,
  lockedProfileExit,
  openPremiumProfileUnlock,
  ProfileLockReason,
  ProfileUnlock,
  type ProfileUnlock as ProfileUnlockName,
} from './profile-access';

const props = withDefaults(
  defineProps<{
    username: string;
    unlocks: ProfileUnlockName[];
    presentation: 'page' | 'modal';
    reason?: ProfileLockReason;
  }>(),
  {
    reason: ProfileLockReason.Profile,
  },
);

const router = useRouter();

const message = computed(() =>
  localizeWithValues(
    props.reason === ProfileLockReason.Invite ? 'profile.lock.handshake.invite' : 'profile.lock.handshake',
    [props.username],
  ),
);
const offersPremium = computed(() => props.unlocks.includes(ProfileUnlock.Premium));

function dismiss() {
  if (props.presentation === 'modal') {
    closeProfileLock();
    return;
  }

  const back = typeof window.history.state?.back === 'string' ? window.history.state.back : null;
  if (lockedProfileExit(back) === 'back') {
    router.back();
    return;
  }

  router.push({ name: RouterNameType.SPARKS });
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.profile-lock {
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-lock.is-page {
  height: 100svh;
  width: 100%;
}

.lock-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-l);
  max-width: 300px;
  width: 100%;
}

.profile-lock.is-modal .lock-card {
  padding: var(--spacing-xxl) var(--spacing-xl) 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.lock-icon {
  color: var(--color-white-30);
}

.lock-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
}

.lock-message {
  margin: 0;
  color: var(--color-white-70);
  line-height: 1.6;
  letter-spacing: 0.015em;
}

.lock-close {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }
}

.profile-lock.is-modal .lock-close {
  position: absolute;
  bottom: var(--spacing-xl);
  left: 50%;
  transform: translateX(-50%);

  @include press($transform: translateX(-50%));
}

.profile-lock.is-page .lock-close {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background-color: var(--color-dark-5);

  @include press($transform: translateX(-50%));
}
</style>
